import * as yup from 'yup'
import { MAX_UPLOADED_IMAGES, MAX_UPLOADED_VIDEOS } from 'constants/upload'
import moment from 'moment'
import { parsePhoneNumberFromString } from 'libphonenumber-js'

export const CreateJobPostScheme = yup.object({
  job_category: yup.string().required('Category is required'),
  isPublished: yup.boolean(),
  price_per_class: yup.string().matches(/^(?=.*\d)\d*$/, 'Price per class is required'),
  price_type: yup.string().required('Price Type is required'),
  job_title: yup
    .string()
    .required('Job Title is required')
    .max(100, 'Text is too long maximum is 100'),
  post_title: yup
    .string()
    .required('Post Title is required')
    .max(100, 'Text is too long maximum is 100'),
  description: yup
    .string()
    .max(3000, 'Text is too long maximum is 3000')
    .required('Description is required'),
  keywords: yup
    .string()
    .max(150, 'Text is too long maximum is 150')
    .required('Keywords is required'),
  images: yup.array().min(1).max(MAX_UPLOADED_IMAGES).required('Image is required'),
  crop: yup.boolean(),
})

export const CreateBannersScheme = yup.object({
  isPublished: yup.boolean(),
  class_banner_category: yup.string().required('Category is required'),
  description: yup
    .string()
    .max(3000, 'Text is too long maximum is 3000')
    .required('Description is required'),
  keywords: yup
    .string()
    .max(150, 'Text is too long maximum is 150')
    .required('Keywords is required'),
  price_per_class: yup.string().matches(/^(?=.*\d)\d*$/, 'Price per class is required'),
  payment_preferences: yup
    .array()
    .min(1, 'At least one preference')
    .required('Payment preference is required'),
  video: yup.array().min(1).max(MAX_UPLOADED_VIDEOS).required('Video is required'),
  images: yup.array().min(1).max(MAX_UPLOADED_IMAGES).required('Image is required'),
})

export const AboutScheme = yup.object({
  profile_picture: yup.mixed().nullable(),

  country_code: yup.string().required('Code area is required'),

  phone_number_part: yup
    .string()
    .required('Phone number is required')
    .matches(/^[0-9]{7,15}$/, 'Please enter a valid phone number')
    .test(
      'valid-phone',
      'Please enter a valid phone number for the selected country',
      function (value) {
        const { country_code } = this.parent
        if (!value || !country_code) return false

        const fullNumber = `${country_code}${value}`
        const phone = parsePhoneNumberFromString(fullNumber)

        return phone?.isValid() || false
      }
    ),

  country_of_origin: yup.string().required('Country of origin is required'),

  country_of_residence: yup.string().required('Country of residence is required'),

  birth_date: yup
    .date()
    .typeError('Birth date is required')
    .required('Birth date is required')
    .test('age-limit', 'You must be at least 18 years old', function (value) {
      const role = this.options.context?.role
      if (!value) return false
      const age = moment().diff(value, 'years')
      if (role === 'tutor') return age >= 18
      return age >= 18
    }), // Added validation to ensure tutors and students are at least 18 years old.

  gender: yup.string().required('Gender is required'),

  user_profile_languages: yup
    .array()
    .min(1, 'There should be at least 1 language')
    .required('Languages is required'),

  time_format: yup.string().required('Please select a time format'),

  timezone: yup.string().required('Timezone is required'),
})

export const GoalScheme = yup.object({
  classes_per_week: yup.string().required('Classes per week is required'),
  class_type: yup
    .array()
    .min(1, 'There should be at least one class type')
    .required('Class type is required'),
  comfortable_time: yup
    .array()
    .min(1, 'There should be at least one comfortable time')
    .required('Comfortable time is required'),
  days_of_week: yup
    .array()
    .min(1, 'There should be at least one weekday')
    .required('Days of week is required'),
  // Use numeric default values for country fields to satisfy backend validation
  tutor_origin_country: yup.number().default(1),
  tutor_residence_country: yup.number().default(1),
})

export const LoginUserScheme = yup.object({
  email: yup.string().email('Invalid e-mail').required('E-mail is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters long'),
})

export const ForgotPasswordScheme = yup.object({
  email: yup.string().email('Invalid e-mail').required('E-mail is required'),
})

export const KYCShema = yup.object({
  country: yup.string().required('Country is required'),
  // documentId: yup.mixed().required('Please supply a valid ID Document'),
})

export const ResetPasswordScheme = yup.object({
  new_password1: yup
    .string()
    .required('New Password is required')
    .min(8, 'New Password must be at least 8 characters long'),
  new_password2: yup
    .string()
    .required('Confirm New Password is required')
    .min(8, 'Confirm New Password must be at least 8 characters long'),
})

export const ProfileTutorScheme = yup.object({
  phone_number_part: yup.string().required('Phone number is required'),
  country_code: yup.string().required('Required'),
})

export const ProfileStudentScheme = yup.object({
  first_name: yup.string().required('First Name is required'),
  last_name: yup.string().required('Last Name is required'),
  gender: yup.string().required('Gender is required'),
  username: yup.string().required('Nickname is required'),
  birth_date: yup.string().required('Birth date is required'),
  phone_number_part: yup.string().required('Phone number is required'),
  country_code: yup.string().required('Required'),
})

export const LanguageScheme = yup.object({
  user_profile_languages: yup.array().min(1, 'There should be at least 1 language'),
})

export const LocationScheme = yup.object({
  country_of_origin: yup.string().required('Country of origin is required'),
  country_of_residence: yup.string().required('Country of residence is required'),
})

export const TimeScheme = yup.object({
  time_format: yup.string().required('Time format is required'),
  timezone: yup.string().required('Timezone is required'),
})

export const ProfileSecurityScheme = yup.object({
  display_name: yup.string().required('Display name is required'),
  display_last_name: yup.string().required('Display last name is required'),
})

export const ChangePasswordScheme = yup.object({
  old_password: yup
    .string()
    .required('Current Password is required')
    .min(8, 'Current Password must be at least 8 characters long'),
  password: yup
    .string()
    .required('New Password is required')
    .min(8, 'New Password must be at least 8 characters long')
    .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
    .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Password must contain at least one special character')
    .matches(/[0-9]/, 'Password must contain at a number')
    .matches(/^(?=\S+$).+/, 'Password must has no space'),
  password2: yup
    .string()
    .required('Confirm New Password is required')
    .oneOf([yup.ref('password')], 'Passwords must match'),
})

export const DeleteAccountScheme = yup.object({
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters long'),
})

export const CreditCardPaymentScheme = yup.object({
  card_number: yup
    .string()
    .required('Card Number is required')
    .matches(/^(\d{4} ){3}\d{4}$/i, 'Card Number is not correct'),
  expires_month: yup
    .string()
    .required('Required')
    .min(2, '2 Characters')
    .max(2, '2 Characters')
    .matches(/^[0-9]*$/, 'Only number'),
  expires_year: yup
    .string()
    .required('Required')
    .min(2, '2 Characters')
    .max(2, '2 Characters')
    .matches(/^[0-9]*$/, 'Only number'),
  security_code: yup
    .string()
    .required('Security Code is required')
    .min(3, 'Must be 3 Characters')
    .max(3, 'Must be 3 Characters')
    .matches(/^[0-9]*$/, 'Must have only number characters'),
})

export const CryptoPaymentScheme = yup.object({
  currency: yup.string().required('Cryptocurrency is required'),
  network: yup.string().required('Network is required'),
})

export const QuizTemplateCreateScheme = yup.object({
  category: yup.string().required('Category is required'),
  quiz_title: yup.string().required('Quiz Title is required'),
  due_date: yup.string().required('Due Date is required'),
})

export const QuizTemplateCreateTaskScheme = yup.object({
  questions: yup
    .array()
    .of(
      yup.object({
        question_text: yup.string().required('Question is required'),
        option1: yup.string().required('Answer is required'),
        option2: yup.string().required('Answer is required'),
        option3: yup.string().required('Answer is required'),
        option4: yup.string().required('Answer is required'),
        correct_option: yup.number().required('Correct Answer is required'),
      })
    )
    .required('Required')
    .min(1, 'At least one question'),
})

export const SwitchCreateAccountSchema = {
  username: yup.string().required('Nickname is required'),
  first_name: yup.string().required('First Name is required'),
  last_name: yup.string().required('Last Name is required'),
}

export const CancelClassSchema = yup.object({
  reason: yup.string().required('Reason is required'),
  reason_description: yup.string().required('Reason is required'),
})
